// Per-route page titles (Phase 34). Routes declare `handle: { title }` in router.tsx; the
// deepest matched handle wins and becomes "<Page> · Quralyst" on route change. Dynamic segments
// (result workspace tabs) carry their tab name in the handle. Titles are supplementary for
// history/bookmarks — the existing route-change focus management stays primary for screen readers.
import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';

interface TitleHandle {
  title?: string;
}

export interface UsePageTitleOptions {
  /**
   * Suppress the route's own title and fall back to the bare app name.
   *
   * Set when a route is being refused with a 404 (admin/role-based-access §2). The whole point of
   * denying with `notFound` is that the page must be indistinguishable from a typo — and the tab
   * title would otherwise announce it, and even name it ("Admin — Organizations · Quralyst" on a
   * page the viewer is not allowed to know exists). A genuine 404 shows "Quralyst", so this does
   * too. Verified in a production build on 2026-09-11.
   *
   * A 403 deliberately does NOT suppress: that denial already tells the user the page exists.
   */
  suppress?: boolean;
}

export function usePageTitle(options: UsePageTitleOptions = {}): void {
  const matches = useMatches();
  const suppress = options.suppress ?? false;

  useEffect(() => {
    if (suppress) {
      document.title = 'Quralyst';
      return;
    }
    const titled = [...matches].reverse().find((m) => (m.handle as TitleHandle)?.title);
    const title = titled ? (titled.handle as TitleHandle).title : undefined;
    document.title = title ? `${title} · Quralyst` : 'Quralyst';
  }, [matches, suppress]);
}
